/**
 * Rule Pattern Utilities
 *
 * Pure functions for working with rule patterns.
 * These are storage-agnostic and can be used by any storage provider
 * (local SQL, remote API, etc.)
 */

/**
 * Minimum pattern length after normalization
 */
export const MIN_PATTERN_LENGTH = 3;

/**
 * Normalize a transaction description into a reusable pattern
 *
 * Removes:
 * - Long numbers (transaction IDs, reference numbers)
 * - Amounts (with or without $)
 * - Dates (MM/DD/YYYY format)
 * - Special characters
 * - Extra whitespace
 *
 * @param description - Transaction description
 * @returns Normalized pattern (lowercase, trimmed)
 *
 * @example
 * normalizePattern('NETFLIX.COM $15.99 #123456')
 * // Returns: 'netflix com'
 *
 * normalizePattern('STARBUCKS STORE #1234 $4.50 12/15/2024')
 * // Returns: 'starbucks store'
 */
export function normalizePattern(description: string): string {
  return description
    .toLowerCase()
    .replace(/\d{4,}/g, '') // Remove long numbers (transaction IDs, dates)
    .replace(/\$?\d+\.?\d*/g, '') // Remove amounts
    .replace(/\d{1,2}\/\d{1,2}\/\d{2,4}/g, '') // Remove dates
    .replace(/[^\w\s]/g, ' ') // Replace special chars with spaces
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
}

/**
 * Check if a pattern is valid (meets minimum length requirement)
 *
 * @param pattern - Pattern to validate
 * @param minLength - Minimum length (default: 3)
 * @returns True if pattern is valid
 *
 * @example
 * isValidPattern('netflix') // true
 * isValidPattern('ab') // false
 * isValidPattern('') // false
 */
export function isValidPattern(pattern: string, minLength: number = MIN_PATTERN_LENGTH): boolean {
  return pattern.length >= minLength;
}

/**
 * Check if a description matches a rule pattern
 *
 * Case-insensitive substring match.
 *
 * @param description - Transaction description to check
 * @param pattern - Rule pattern to match against
 * @returns True if description matches pattern
 *
 * @example
 * matchesPattern('NETFLIX.COM MONTHLY', 'netflix')
 * // Returns: true
 *
 * matchesPattern('STARBUCKS #1234', 'amazon')
 * // Returns: false
 */
export function matchesPattern(description: string, pattern: string): boolean {
  const lowerDesc = description.toLowerCase();
  const lowerPattern = pattern.toLowerCase();
  return lowerDesc.includes(lowerPattern);
}

/**
 * Find the first matching pattern from a list of patterns
 *
 * @param description - Transaction description
 * @param patterns - Array of patterns to check
 * @returns First matching pattern or null
 *
 * @example
 * findMatchingPattern('NETFLIX.COM', ['amazon', 'netflix', 'spotify'])
 * // Returns: 'netflix'
 *
 * findMatchingPattern('WALMART', ['target', 'costco'])
 * // Returns: null
 */
export function findMatchingPattern(description: string, patterns: string[]): string | null {
  for (const pattern of patterns) {
    if (matchesPattern(description, pattern)) {
      return pattern;
    }
  }
  return null;
}

/**
 * Normalize and validate a pattern in one step
 *
 * @param description - Transaction description
 * @param minLength - Minimum pattern length (default: 3)
 * @returns Object with normalized pattern and validity flag
 *
 * @example
 * normalizeAndValidate('NETFLIX $15.99 #123')
 * // Returns: { pattern: 'netflix', valid: true }
 *
 * normalizeAndValidate('AB #123')
 * // Returns: { pattern: 'ab', valid: false }
 */
export function normalizeAndValidate(
  description: string,
  minLength: number = MIN_PATTERN_LENGTH
): { pattern: string; valid: boolean; reason?: string } {
  const pattern = normalizePattern(description);
  const valid = isValidPattern(pattern, minLength);

  if (!valid && pattern.length === 0) {
    return { pattern, valid, reason: 'Pattern is empty after normalization' };
  }

  if (!valid) {
    return {
      pattern,
      valid,
      reason: `Pattern too short (${pattern.length} chars, minimum ${minLength})`
    };
  }

  return { pattern, valid };
}

/**
 * Get pattern suggestions by finding common words across descriptions
 *
 * @param descriptions - Array of transaction descriptions
 * @param minOccurrences - Minimum number of occurrences (default: 2)
 * @returns Array of suggested patterns sorted by frequency
 *
 * @example
 * getPatternSuggestions([
 *   'NETFLIX.COM $15.99',
 *   'NETFLIX.COM $15.99',
 *   'SPOTIFY PREMIUM $9.99'
 * ])
 * // Returns: ['netflix', 'spotify']
 */
export function getPatternSuggestions(
  descriptions: string[],
  minOccurrences: number = 2
): string[] {
  const wordCounts = new Map<string, number>();

  for (const desc of descriptions) {
    const normalized = normalizePattern(desc);
    const words = normalized.split(/\s+/).filter(w => w.length >= MIN_PATTERN_LENGTH);

    for (const word of words) {
      wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
    }
  }

  // Filter by min occurrences and sort by frequency
  const suggestions = Array.from(wordCounts.entries())
    .filter(([_, count]) => count >= minOccurrences)
    .sort((a, b) => b[1] - a[1])
    .map(([word, _]) => word);

  return suggestions;
}
